/**
 * Carousel prototype list — `/prototypes/carousels`
 *
 * Multi-slide social carousel documents. Each slide is an ImageEditor document;
 * the carousel owns the story arc, ordering, caption, versions, and exports.
 *
 * @feature-spec brain/vidmax/feature-specs/prototypes-carousels.md
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@aix/ui"
import { Images, Plus } from "lucide-react"

import CustomHead from "~/components/SEOHead/CustomHead"
import { PrototypeLayout } from "~/layouts/PrototypeLayout"
import { api } from "~/utils/api"
import {
  DEFAULT_CAROUSEL_SLIDE_COUNT,
  ECarouselGoal,
  ECarouselPlatform,
} from "@aix/vidmax-shared/src/constants/imageCarousel"
import type { ImageEditAspect } from "@aix/vidmax-shared/src/constants/imageEditor"

const platformLabel = (platform: string) => (platform === ECarouselPlatform.LINKEDIN ? "LinkedIn" : "Instagram")
const goalLabel = (goal: string) => goal.split("_").join(" ")

function NewCarouselDialog() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [topic, setTopic] = useState("Turn one customer insight into a repeatable content engine")
  const [platform, setPlatform] = useState<ECarouselPlatform>(ECarouselPlatform.LINKEDIN)
  const [goal, setGoal] = useState<ECarouselGoal>(ECarouselGoal.THOUGHT_LEADERSHIP)
  const [aspectRatio, setAspectRatio] = useState<ImageEditAspect>("4:5")
  const [slideCount, setSlideCount] = useState(DEFAULT_CAROUSEL_SLIDE_COUNT)
  const [audience, setAudience] = useState("founders and growth teams")
  const [tone, setTone] = useState("sharp, practical, credible")
  const [cta, setCta] = useState("Save this and build your next post from the first slide.")

  const create = api.carouselEditor.createFromPrompt.useMutation({
    onSuccess: ({ carouselId }) => {
      setOpen(false)
      toast.success("Carousel created")
      void navigate({ to: "/prototypes/carousels/$carouselId", params: { carouselId } })
    },
    onError: (e) => toast.error(e.message),
  })

  const submit = () => {
    create.mutate({ topic, platform, goal, aspectRatio, slideCount, audience, tone, cta })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1.5 h-4 w-4" />
          New carousel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New carousel</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 pt-2">
          <div className="space-y-1">
            <Label>Topic</Label>
            <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>Platform</Label>
              <Select value={platform} onValueChange={(value) => setPlatform(value as ECarouselPlatform)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ECarouselPlatform.LINKEDIN}>LinkedIn</SelectItem>
                  <SelectItem value={ECarouselPlatform.INSTAGRAM}>Instagram</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Goal</Label>
              <Select value={goal} onValueChange={(value) => setGoal(value as ECarouselGoal)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ECarouselGoal.THOUGHT_LEADERSHIP}>Thought leadership</SelectItem>
                  <SelectItem value={ECarouselGoal.EDUCATE}>Educate</SelectItem>
                  <SelectItem value={ECarouselGoal.SELL}>Sell</SelectItem>
                  <SelectItem value={ECarouselGoal.LAUNCH}>Launch</SelectItem>
                  <SelectItem value={ECarouselGoal.CASE_STUDY}>Case study</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Aspect</Label>
              <Select value={aspectRatio} onValueChange={(value) => setAspectRatio(value as ImageEditAspect)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4:5">4:5</SelectItem>
                  <SelectItem value="1:1">1:1</SelectItem>
                  <SelectItem value="9:16">9:16</SelectItem>
                  <SelectItem value="16:9">16:9</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
            <div className="space-y-1">
              <Label>Slides</Label>
              <Input
                type="number"
                min={3}
                max={12}
                value={String(slideCount)}
                onChange={(e) => setSlideCount(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label>Audience</Label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Tone</Label>
            <Input value={tone} onChange={(e) => setTone(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>CTA</Label>
            <Input value={cta} onChange={(e) => setCta(e.target.value)} />
          </div>
          <Button onClick={submit} disabled={!topic.trim() || create.isLoading}>
            {create.isLoading ? "Creating..." : "Create carousel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function CarouselList() {
  const navigate = useNavigate()
  const carouselsQuery = api.carouselEditor.list.useQuery({ limit: 50 })

  const carousels = carouselsQuery.data ?? []

  return (
    <div className="mx-auto max-w-6xl p-6">
      <CustomHead title="Carousel Prototype" />
      <div className="mb-5 flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Images className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-xl font-semibold">Carousels</h1>
          </div>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Create ordered Instagram or LinkedIn image strings with slide copy, image prompts, captions, and exportable image assets.
          </p>
        </div>
        <NewCarouselDialog />
      </div>

      {carouselsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : carousels.length === 0 ? (
        <div className="rounded border border-dashed p-8 text-center">
          <p className="text-sm font-medium">No carousels yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Create one from a topic and use the editor to tune each slide.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {carousels.map((carousel) => (
            <Card
              key={carousel.id}
              className="cursor-pointer overflow-hidden transition hover:ring-2 hover:ring-primary"
              onClick={() => void navigate({ to: "/prototypes/carousels/$carouselId", params: { carouselId: carousel.id } })}
            >
              <div className="flex aspect-[4/3] items-center justify-center bg-neutral-950">
                {carousel.previewUrl ? (
                  <img src={carousel.previewUrl} alt={carousel.name ?? "carousel"} className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-24 w-36 grid-cols-3 gap-1">
                    {Array.from({ length: Math.min(carousel.slideCount, 9) }, (_, index) => (
                      <div key={index} className="rounded-sm border border-white/15 bg-white/10" />
                    ))}
                  </div>
                )}
              </div>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{carousel.name ?? "Untitled carousel"}</p>
                    <p className="text-xs text-muted-foreground">{new Date(carousel.updatedAt).toLocaleDateString()}</p>
                  </div>
                  <Badge variant="secondary">{carousel.aspectRatio}</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  <Badge variant="outline">{platformLabel(carousel.platform)}</Badge>
                  <Badge variant="outline">{goalLabel(carousel.goal)}</Badge>
                  <Badge variant="outline">{carousel.slideCount} slides</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function Wrapper() {
  return (
    <PrototypeLayout>
      <CarouselList />
    </PrototypeLayout>
  )
}

export const Route = createFileRoute("/prototypes/carousels/")({ component: Wrapper })
